import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Check } from "lucide-react";
import { createContactMessage, listSiteContent } from "@/lib/api";
import HeroSlideshow from "@/components/HeroSlideshow";
import { useSwipeToNavigate } from "@/components/MobilePageDots";
import { SocialIcon, getPlatform } from "@/lib/socials";

const DEFAULT_HERO = {
  overline: "Contact",
  title_top: "Say",
  title_italic: "hello.",
  body:
    "Private events, media & press, larger parties, or the simple joy of hearing from a fellow lover of Sri Lankan food — we'd love to hear from you.",
};

const DEFAULT_INFO = {
  phone: "+62 21 1234 5678",
  hours_label: "Daily, 12:00 – 22:00",
  email_general: "hello@nelumdining.com",
  email_events: "events@nelumdining.com",
  email_press: "press@nelumdining.com",
  address_line1: "Jl. Suryo No. 24",
  address_line2: "Senopati, South Jakarta",
  socials: [],
};

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [info, setInfo] = useState(DEFAULT_INFO);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await listSiteContent();
        if (cancelled) return;
        const h = items.find((c) => c.section === "contact-hero");
        const i = items.find((c) => c.section === "contact-info");
        if (h) setHero({ ...DEFAULT_HERO, ...h });
        if (i) setInfo({ ...DEFAULT_INFO, ...i });
      } catch (err) {
        console.warn("[Contact] Failed to load site content — using defaults", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const inputCls =
    "w-full bg-transparent border-b border-border rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors duration-300 font-sans";

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name || !email || !subject || !message) {
      toast.error("Please complete all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await createContactMessage({ name, email, subject, message });
      setSent(true);
      toast.success("Message sent. We'll be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const telHref = info.phone ? `tel:${info.phone.replace(/[^+\d]/g, "")}` : "#";

  return (
    <div data-testid="page-contact" {...useSwipeToNavigate()} className="touch-pan-y">
      {/* Header */}
      <section className="relative border-b border-border min-h-[70vh] flex items-center">
        <HeroSlideshow page="contact" />
        <div className="relative z-10 w-full mx-auto max-w-[1400px] px-6 lg:px-12 py-32 lg:py-40 fade-up">
          <div className="overline">{hero.overline || "Contact"}</div>
          {hero.title_visible !== false && (
            <h1
              data-testid="contact-hero-title"
              className="mt-8 serif text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] tracking-tighter text-foreground [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]"
            >
              {hero.title_top || "Say"}{" "}
              <span className="italic text-foreground">{hero.title_italic || "hello."}</span>
            </h1>
          )}
          {hero.body_visible !== false && hero.body && (
            <p
              data-testid="contact-hero-body"
              className="mt-8 max-w-xl text-sm md:text-base leading-relaxed text-foreground/90 [text-shadow:0_1px_10px_rgba(0,0,0,0.5)] whitespace-pre-line"
            >
              {hero.body}
            </p>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Left: contact info */}
        <div className="lg:col-span-5 space-y-14">
          {info.phone && (
            <div>
              <div className="overline flex items-center gap-2">
                <Phone size={12} strokeWidth={1.5} /> Reservations
              </div>
              <a
                href={telHref}
                data-testid="contact-info-phone"
                className="mt-6 block serif text-3xl md:text-4xl font-light text-foreground hover:text-primary transition-colors"
              >
                {info.phone}
              </a>
              {info.hours_label && (
                <div className="mt-2 text-sm text-muted-foreground">{info.hours_label}</div>
              )}
            </div>
          )}

          {(info.email_general || info.email_events || info.email_press) && (
            <div>
              <div className="overline flex items-center gap-2">
                <Mail size={12} strokeWidth={1.5} /> Email
              </div>
              <div className="mt-6 space-y-4 text-sm">
                {info.email_general && (
                  <div>
                    <div className="text-muted-foreground text-xs uppercase tracking-[0.18em]">General</div>
                    <a
                      href={`mailto:${info.email_general}`}
                      data-testid="contact-info-email-general"
                      className="mt-1 block text-lg text-foreground hover:text-primary transition-colors"
                    >
                      {info.email_general}
                    </a>
                  </div>
                )}
                {info.email_events && (
                  <div>
                    <div className="text-muted-foreground text-xs uppercase tracking-[0.18em]">Private Events</div>
                    <a
                      href={`mailto:${info.email_events}`}
                      data-testid="contact-info-email-events"
                      className="mt-1 block text-lg text-foreground hover:text-primary transition-colors"
                    >
                      {info.email_events}
                    </a>
                  </div>
                )}
                {info.email_press && (
                  <div>
                    <div className="text-muted-foreground text-xs uppercase tracking-[0.18em]">Press</div>
                    <a
                      href={`mailto:${info.email_press}`}
                      data-testid="contact-info-email-press"
                      className="mt-1 block text-lg text-foreground hover:text-primary transition-colors"
                    >
                      {info.email_press}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {(info.address_line1 || info.address_line2) && (
            <div>
              <div className="overline flex items-center gap-2">
                <MapPin size={12} strokeWidth={1.5} /> Visit
              </div>
              <div className="mt-6 text-lg text-foreground/90 leading-relaxed">
                {info.address_line1}
                {info.address_line1 && info.address_line2 && <br />}
                {info.address_line2}
              </div>
            </div>
          )}

          {(info.socials || []).some((s) => s.is_active && s.url) && (
            <div>
              <div className="overline">Follow</div>
              <div className="mt-6 flex items-center flex-wrap gap-x-6 gap-y-3">
                {(info.socials || [])
                  .filter((s) => s.is_active && s.url)
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((s) => {
                    const p = getPlatform(s.platform);
                    return (
                      <a
                        key={s.id || `${s.platform}-${s.url}`}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`contact-social-${s.platform}`}
                        className="inline-flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors"
                      >
                        <SocialIcon platform={s.platform} size={16} />
                        <span className="text-sm">{s.handle || p.label}</span>
                      </a>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          {sent ? (
            <div
              data-testid="contact-success"
              className="border border-primary/50 bg-card p-10 fade-up"
            >
              <div className="w-12 h-12 border border-primary flex items-center justify-center text-primary">
                <Check size={20} strokeWidth={1.5} />
              </div>
              <div className="overline mt-6">Received</div>
              <h2 className="mt-4 serif text-4xl md:text-5xl font-light text-foreground leading-tight">
                Thank you.
              </h2>
              <p className="mt-6 text-sm text-muted-foreground leading-relaxed max-w-lg">
                Your message has arrived. Our team responds within one business
                day — sooner, if the kettle is already on.
              </p>
              <button
                data-testid="contact-send-another-btn"
                onClick={() => {
                  setSent(false);
                  setName("");
                  setEmail("");
                  setSubject("");
                  setMessage("");
                }}
                className="mt-10 inline-flex items-center gap-3 px-7 py-4 border border-primary text-primary text-[0.72rem] uppercase tracking-[0.24em] font-semibold hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                Send another
              </button>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="space-y-12"
            >
              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <label className="overline block mb-4">Your Name</label>
                  <input
                    data-testid="contact-name-input"
                    className={inputCls}
                    placeholder="e.g. Priya Wijeratne"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="overline block mb-4">Email</label>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    className={inputCls}
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="overline block mb-4">Subject</label>
                <input
                  data-testid="contact-subject-input"
                  className={inputCls}
                  placeholder="Private dinner for 12, press enquiry, feedback…"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="overline block mb-4">Message</label>
                <textarea
                  data-testid="contact-message-input"
                  className={inputCls + " resize-none"}
                  rows={6}
                  placeholder="Tell us more…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
              </div>

              <div className="pt-4 flex flex-wrap items-center gap-6">
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.24em] font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? "Sending…" : "Send Message"}
                </button>
                <span className="text-xs text-muted-foreground italic">
                  We reply within one business day.
                </span>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
